import { balanceAccountName } from './balanceNaming';
import { novigAccounts, antNovigAccounts } from './novigAccounts';

export const balanceGroups = [
  { key: 'copytraders', name: 'Copy traders', description: 'Fido CT and Matt CT' },
  { key: 'sniping', name: 'Sniping', description: 'Sniping bots' },
  { key: 'arbitrage', name: 'Arbitrage', description: 'ARB strategies' },
  { key: 'main', name: 'Main accounts / fills', description: 'Primary accounts used for fills' },
  { key: 'market-makers', name: 'Market makers', description: 'MM and MM LIVE' },
];
export const balanceConnections = [
  { provider: 'eddy-kalshi', platform: 'Kalshi', owner: 'Eddy', strategy: 'Matt CT', role: 'copytraders' },
  { provider: 'polyus-dk', platform: 'PolyUS', owner: 'DK', strategy: 'Main', role: 'main' },
  { provider: 'ant-polyus-inst', platform: 'PolyUS', owner: 'Anthony Inst', strategy: 'Matt CT', role: 'copytraders' },
  { provider: 'ant-prophetx', platform: 'ProphetX', owner: 'Anthony', strategy: 'Matt CT', role: 'copytraders' },
  { provider: 'yan-prophetx', platform: 'ProphetX', owner: 'Yan', strategy: 'ARB', role: 'arbitrage' },
  { provider: 'yan-kalshi', platform: 'Kalshi', owner: 'Yan', strategy: 'Jalen Snipe', role: 'sniping' },
  { provider: 'novig', platform: 'NoVig', owner: 'DK', wallets: novigAccounts },
  { provider: 'ant-novig', platform: 'NoVig', owner: 'Anthony', wallets: antNovigAccounts },
  { provider: 'prophetx', platform: 'ProphetX', owner: 'DK', strategy: 'Main', role: 'main' },
  { provider: 'kalshi', platform: 'Kalshi', owner: 'DK', strategy: 'Main', role: 'main' },
  { provider: 'greg-polyus', platform: 'PolyUS', owner: 'Greg', strategy: 'MM', role: 'market-makers' },
  { provider: 'greg-kalshi', platform: 'Kalshi', owner: 'Greg', strategy: 'MM', role: 'market-makers' },
  { provider: 'ant-kalshi', platform: 'Kalshi', owner: 'Anthony', strategy: 'Fido CT', role: 'copytraders' },
  { provider: 'polyus-fido-ct', platform: 'PolyUS', owner: 'DK Inst', strategy: 'Fido CT', role: 'copytraders' },
].map(connection => ({ ...connection, name: balanceAccountName(connection) }));
const accounts = balanceConnections.flatMap(connection => connection.wallets
  ? connection.wallets.map(wallet => ({ key: `${connection.provider}:${wallet.id}`, provider: connection.provider, group: connection.name, name: wallet.name, role: wallet.role, accountId: wallet.id, href: `/admin/${connection.provider}-balances/${wallet.slug}` }))
  : [{ key: connection.provider, provider: connection.provider, group: connection.name, role: connection.role, name: connection.name, href: `/admin/${connection.provider}-balances` }]);
export const overviewAccounts = balanceGroups.flatMap(group => accounts.filter(account => account.role === group.key));
export const balanceName = provider => balanceConnections.find(connection => connection.provider === provider)?.name;
export const groupedAccounts = rows => balanceGroups.map(group => ({ ...group, rows: rows.filter(row => row.role === group.key) })).filter(group => group.rows.length);
// Preserve the same balance order in both overview tables. Missing balances sort last.
export function sortedBalanceGroups(rows) {
  const descending = (a, b) => a === null ? (b === null ? 0 : 1) : b === null ? -1 : b - a;
  const value = row => row.live ? moneyUnits(row.live.total) : null;
  return groupedAccounts(rows)
    .map(group => ({ ...group, rows: [...group.rows].sort((a, b) => descending(value(a), value(b))) }))
    .sort((a, b) => descending(groupTotal(a.rows, 'live', 'total'), groupTotal(b.rows, 'live', 'total')));
}
export function groupTotal(rows, source, field) {
  return rows.length > 0 && rows.every(row => row[source] && row[source][field] !== undefined)
    ? sumMoney(rows.map(row => row[source][field])) : null;
}
export function groupFunding(rows, field) {
  return rows.length > 0 && rows.every(row => row.week?.funding?.[field] !== undefined)
    ? sumMoney(rows.map(row => row.week.funding[field])) : null;
}

// Keep six decimal places until the final currency display, matching the reports.
export function moneyUnits(value) {
  const match = /^(-?)(\d+)(?:\.(\d{1,6}))?$/.exec(String(value));
  if (!match) throw new Error('Invalid balance amount');
  const units = Number(match[2]) * 1000000 + Number((match[3] || '').padEnd(6, '0'));
  if (!Number.isSafeInteger(units)) throw new Error('Balance amount is too large');
  return match[1] ? -units : units;
}
export function sumMoney(values) {
  const units = values.reduce((sum, value) => sum + moneyUnits(value), 0);
  if (!Number.isSafeInteger(units)) throw new Error('Combined balance is too large');
  return units / 1000000;
}
const shiftDate = (date, days) => { const value = new Date(`${date}T12:00:00Z`); value.setUTCDate(value.getUTCDate() + days); return value.toISOString().slice(0, 10); };
export function overviewPeriod(now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(now).map(part => [part.type, part.value]));
  let day = `${parts.year}-${parts.month}-${parts.day}`;
  if (Number(parts.hour) < 1) day = shiftDate(day, -1);
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
  return { openingDate: shiftDate(day, -(weekday === 0 ? 7 : weekday)), closingDate: shiftDate(day, -1) };
}
export function completedWeekPeriod(now = new Date()) {
  const closingDate = overviewPeriod(now).openingDate;
  return { openingDate: shiftDate(closingDate, -7), closingDate };
}
export function weeklySummary(report, account, period) {
  if (!report || report.stale) return null;
  const close = report.days?.find(day => day.date === period.closingDate);
  const closing = account.accountId ? close?.accounts.find(row => row.id === account.accountId) : close?.totals;
  if (!closing) return null;
  const status = close.snapshot?.status || 'reconstructed';
  if (period.openingDate === period.closingDate) return { opening: closing.total, closing: closing.total, pnl: '0', funding: { deposits: '0', withdrawals: '0', externalNet: '0', transferNet: '0' }, status };
  const week = report.weeks?.find(row => row.openingDate === period.openingDate && row.closingDate === period.closingDate);
  const summary = week?.accounts.find(row => row.id === (account.accountId || 'all'));
  return summary ? { ...summary, status } : null;
}
export function liveAccount(snapshot, account) {
  if (!snapshot) return null;
  const row = account.accountId ? snapshot.accounts?.find(item => item.id === account.accountId) : snapshot.totals;
  if (!row) return null;
  // An incomplete or malformed source must never look like a zero balance.
  ['cash', 'positionCost', 'total'].forEach(field => moneyUnits(row[field]));
  return row;
}
